import { useEffect, useMemo, useState } from "react"
import { useAuthContext } from "context/AuthContext"
import { useLocation, useNavigate } from "react-router-dom"

import BattleRecord from "component/MyPage/BattleRecord"
import GameHistoryList from "component/MyPage/GameHistoryList"
import MyPageTop from "component/MyPage/MyPageTop"

import { User } from "model/User"
import { Game } from "model/Game"
import { Season } from "model/Season"

import * as userRepository from 'repository/UserRepository'
import * as gameRepository from 'repository/GameRepository'
import * as seasonRepository from 'repository/SeasonRepository'

export default function MyPageRoot() {
  const navigate = useNavigate()
  const { auth } = useAuthContext()
  const { search } = useLocation()

  const query = useMemo(() => new URLSearchParams(search), [search])
  const userId = query.get('id')

  const [user, setUser] = useState<User | null>(null)
  useEffect(() => {
    if (!userId) {
      navigate('/')
      return
    }

    userRepository.findFromCache(userId)
      .then(it => {
        setUser(it)
        setShowing('Top')
      })
      .catch(() => navigate('/'))
  }, [userId])

  const [me, setMe] = useState<User | null>(null)
  const [isOwnPage, setIsOwnPage] = useState(false)
  useEffect(() => {
    if (!auth) return
    if (!user) return

    const isOwn = user.uuid === auth.uid
    setIsOwnPage(isOwn)

    if (isOwn) {
      setMe(user)
    } else {
      userRepository.findFromCache(auth.uid).then(it => setMe(it))
    }
  }, [auth, user])

  const [seasons, setSeasons] = useState<Season[]>([])
  const [season, setSeason] = useState<Season | null>(null)
  useEffect(() => {
    seasonRepository
      .listFromCache(undefined, [{ key: 'end_time', condition: 'desc' }])
      .then(it => {
        const target = it.filter(_it => _it.start_time <= new Date().getTime())
        setSeasons(target)
        setSeason(target[0])
      })
    }, [])

  const [games, setGames] = useState<Game[]>([])
  useEffect(() => {
    if (!auth) return
    if (!user) return
    if (!season) return

    if (auth.uid !== user.uuid &&
      !user.open_game_history &&
      !user.open_winrate_by_decktype &&
      !user.open_rate_transition) return

    gameRepository
      .listFromServer(
        [
          { key: 'season.id', condition: '==', value: season.id },
          { key: 'player_uuids', condition: 'array-contains', value: user.uuid }
        ],
        [{ key: 'start_time', condition: 'desc' }],
      )
      .then(it => {
        const target = it.filter(it => it.end_time)
        setGames(target)
      })
  }, [auth, user, season])

  const [showing, setShowing] = useState<'Top' | 'History' | 'Record'>('Top')
  const [gamesSpecificDeckType, setGamesSpecificDeckType] = useState<Game[]>([])

  if (!user) return (<></>)
  return (
    <>
      {showing === 'Top' &&
        <MyPageTop
          isSubscriber={!!(me?.is_subscriber)}
          isOwnPage={isOwnPage}
          user={user}
          seasons={seasons}
          season={season!}
          games={games}
          setShowing={val => setShowing(val)}
          setGamesSpecificDeckType={games => setGamesSpecificDeckType(games)}
          updateUser={(user) => setUser(user)}
          changeSeason={(season) => setSeason(season)}
        />
      }
      {showing === 'History' &&
        <GameHistoryList
          isSubscriber={!!(me?.is_subscriber)}
          isOwnPage={isOwnPage}
          user={user}
          games={games}
          backToTop={() => setShowing('Top')}
        />
      }
      {showing === 'Record' &&
        <BattleRecord
          user={user}
          games={gamesSpecificDeckType}
          backToTop={() => setShowing('Top')}
        />
      }
    </>
  )
}